import React, { useCallback, useRef } from 'react';
import { IApiMessageDto, IApiSubMessageDto } from '@/services/swagger/easourcing/models';
import { useChatPageStore } from '../../store';
import {
  DraftEmailSubMsg,
  InquiryTaskCompleteSubMsg,
  MarketInsightCardSubMsg,
  ReplaySubMsg,
  SubContentType,
  InsightChipSubMsg,
} from '../../types';
import { SubMsgBaseProps } from '../../types/comp-block';
import { MarkdownBlock } from '../markdown-block';
import { SupplierChatListCard } from '../supplier/supplier-chat-list-card';
import { SearchCardV2SubMsg } from '../../types';
import { RequestSessionCard } from '@/components/request-session-card';
import { InqueryTaskFinishSystemMsg } from './system-msg/inqiry-task-complete-msg';
import { ReplySystemMsg } from './system-msg/reply-msg';
import { EmailPreviewWithCard } from './reply-preview-card';
import { MarketInsightCard } from './market-insight/card';
import { createMarketInsightStore } from './market-insight/card/store';
import type { MarketInsightCardContainerProps } from './market-insight/card/store-types';
import useMarketInsightPanelStore from '../../store/market-insight-panel-store';
import InsightChips from './insight-chips';
import { SearchCardV2Card } from '../supplier/search-card-v2';

// ─────────────────────────────────────────────
// 行业报告卡片 + 详情侧面板（封装在一起以使用 Hooks）
// ─────────────────────────────────────────────

/**
 * MarketInsightCardWithDetail
 *
 * 将 MarketInsightCard 与右侧详情面板联动：
 * - 使用 useRef 持久化 store 实例（生命周期与组件一致）
 * - 点击 View Details 时，通过 marketInsightPanelStore 激活右侧面板（3列布局）
 * - Card 与 Detail 通过同一 store 实例共享 isSaved / followStatus / isDetailOpen
 */
function MarketInsightCardWithDetail({
  payload,
  isLoading,
  hasError,
  isLastedMessage,
  messageId,
}: MarketInsightCardContainerProps & { isLastedMessage: boolean; messageId: string }) {
  const allowAutoOpenDetail = useChatPageStore(s => s.allowAutoOpenDetail);
  const setStoreState = useChatPageStore(s => s.setStoreState);
  const getStoreState = useChatPageStore(s => s.getStoreState);
  const triggerInputTooltip = useChatPageStore(s => s.triggerInputTooltip);
  const setActiveStore = useMarketInsightPanelStore(s => s.setActiveStore);

  const handleDetailOpen = useCallback(() => {
    if (storeRef.current) {
      setActiveStore(storeRef.current);
    }
  }, [setActiveStore]);

  // 维护 store 实例，仅在 payload 就绪时创建，随组件生命周期 GC
  const storeRef = useRef<ReturnType<typeof createMarketInsightStore> | null>(null);
  if (!storeRef.current && !isLoading && !hasError && payload) {
    storeRef.current = createMarketInsightStore({
      ...payload,
      onDetailLoadingChange: (loading, reportId) => {
        const { reportDetailLoadingMap, sseMessageIds } = getStoreState();
        setStoreState({ reportDetailLoadingMap: { ...reportDetailLoadingMap, [reportId]: loading } });
        // 详情加载完成 + 是最后一条消息 + 是 SSE 消息 → 自动打开详情面板 + 触发输入框 tooltip
        if (!loading && isLastedMessage && sseMessageIds.has(messageId)) {
          if (allowAutoOpenDetail) {
            handleDetailOpen();
            triggerInputTooltip();
          }
        }
      },
    });
    // 初始化时立即拉取报告详情（卡片数据来自 API，payload 仅含轻量字段）
    storeRef.current.getState().fetchDetail();
  }

  return (
    /*
     * 通过 externalStore 将外部 store 注入 Card，
     * 确保 Card 与 Detail 使用同一 store 实例，状态完全共享
     */
    <MarketInsightCard
      payload={payload}
      isLoading={isLoading}
      hasError={hasError}
      externalStore={storeRef.current ?? undefined}
      onDetailOpen={handleDetailOpen}
    />
  );
}

// ─────────────────────────────────────────────
// SubMessage 渲染配置
// ─────────────────────────────────────────────

type SubMsgRenderer = (item: IApiSubMessageDto, ctx: SubMsgBaseProps) => React.ReactNode;

interface SubMessageConfigItem {
  match: (item: IApiSubMessageDto) => boolean;
  render: SubMsgRenderer;
}

const hiddenFeedbackSubContentTypeSet = new Set([
  SubContentType.taskComplete,
  SubContentType.reply,
  SubContentType.inquiryCard,
]);
/**
 * 是否隐藏反馈
 * 1. 没有子消息，返回true
 * 2. 子消息中包含任务完成或收到邮件回复，返回true
 * 3. 其他情况，返回false
 */
export const hiddenFeedback = (msg: IApiMessageDto) => {
  if (!msg.subMessage) return true;
  if (msg.subMessage.length === 0) return true;
  return msg.subMessage.some(item => hiddenFeedbackSubContentTypeSet.has(item.contentType as SubContentType));
};

export const subMessageConfig: SubMessageConfigItem[] = [
  {
    /** 任务完成 */
    match: item => item.contentType === SubContentType.taskComplete,
    render: (item, ctx) => (
      <InqueryTaskFinishSystemMsg
        dialogWrpperRef={ctx.dialogWrpperRef}
        sessionId={ctx.sessionId}
        subMsg={item as InquiryTaskCompleteSubMsg}
      />
    ),
  },
  {
    /** 收到邮件回复 */
    match: item => item.contentType === SubContentType.reply,
    render: (item, ctx) => (
      <ReplySystemMsg dialogWrpperRef={ctx.dialogWrpperRef!} sessionId={ctx.sessionId} subMsg={item as ReplaySubMsg} />
    ),
  },
  {
    /** 思考过程 — 文本由后端 SUB_MESSAGE_CHUNK 驱动实时更新 */
    match: item => item.contentType === SubContentType.thought,
    render: item => <MarkdownBlock markdown={item.contentValue?.text || ''} />,
  },
  {
    /** 内容 — 文本由后端 SUB_MESSAGE_CHUNK 驱动实时更新 */
    match: item => item.contentType === SubContentType.content,
    render: item => <MarkdownBlock markdown={item.contentValue?.text || ''} />,
  },
  {
    /** 搜索卡片 */
    match: item => item.contentType === SubContentType.searchCard,
    render: (item, ctx) => (
      <SupplierChatListCard
        sessionId={ctx.sessionId}
        subMsgData={item}
        messageId={ctx.messageId}
        dialogWrpperRef={ctx.dialogWrpperRef}
      />
    ),
  },
  {
    /** 搜索卡片 V2 — 使用 SearchCardV2Card 纯展示，数据结构与后端 SEARCH_RESULT_CARD payload 对齐 */
    match: item => item.contentType === SubContentType.searchCardV2,
    render: (item, ctx) => {
      const subMsg = item as SearchCardV2SubMsg;
      if (!subMsg.contentValue) return null;
      // key 策略：subMessageId + companyList.length 变化时强制重新实例化组件（FR-010），
      // 消除 submessage 更新后组件内部的副作用状态残留。
      return <SearchCardV2Card subMsg={subMsg} sessionId={ctx.sessionId} messageId={ctx.messageId} />;
    },
  },
  {
    /** inquiry卡片 */
    match: item => item.contentType === SubContentType.inquiryCard,
    render: (item, ctx) => {
      const { subMessageId, contentValue } = item;
      if (!subMessageId) return null;
      return (
        <div className="flex flex-col gap-3">
          {contentValue?.text && <div className="text-[14px] text-[#171717] leading-[22px]">{contentValue.text}</div>}
          <RequestSessionCard
            subMessageId={subMessageId}
            sessionId={ctx.sessionId}
            dialogWrpperRef={ctx.dialogWrpperRef}
            className="mt-0"
          />
        </div>
      );
    },
  },
  {
    /** AI 草稿邮件 */
    match: item => item.contentType === SubContentType.emailSuggestion,
    render: (item, ctx) => {
      const itemV2 = item as DraftEmailSubMsg;
      return (
        <EmailPreviewWithCard
          {...ctx}
          subject={itemV2.contentValue?.emailSubject || ''}
          fullContent={itemV2.contentValue?.text || ''}
          summaryContent={itemV2.contentValue?.emailSummary || ''}
        />
      );
    },
  },
  {
    /** 行业报告卡片（Market Trend / Company Audit） */
    match: item => item.contentType === SubContentType.marketInsightReport,
    render: (item, ctx) => {
      const subMsg = item as MarketInsightCardSubMsg;
      const cv = subMsg.contentValue;

      // payload 不完整时（后端数据异常）渲染错误态
      if (!cv?.reportId || !cv?.title) {
        return <MarketInsightCard payload={cv as MarketInsightCardContainerProps['payload']} hasError />;
      }

      return (
        <MarketInsightCardWithDetail
          payload={cv}
          isLoading={false}
          hasError={false}
          isLastedMessage={ctx.isLastedMessage}
          messageId={ctx.messageId}
        />
      );
    },
  },
  {
    /**
     * Insight Chip 列表
     * 由轮询 /api/biz/session/insight-chip 就绪后插入，渲染为可点击的快捷操作 chip。
     * 渲染由 AiMessageComponent 在 MessageFeedback 之后调用（位置在点赞/点踩下方），
     * otherSubMsgs.map 中会跳过此类型以防重复渲染。
     */
    match: item => item.contentType === SubContentType.insightChip,
    render: (item, ctx) => {
      const subMsg = item as InsightChipSubMsg;
      const items = subMsg.contentValue?.chips ?? [];
      return (
        <InsightChips items={items} onSelect={text => ctx.onSomeAction('sendChipMessage', { text })} className="mt-3" />
      );
    },
  },
];
